import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import moment from "moment";
import toast from "react-hot-toast";
import Select from "react-select";
import { FiFilter, FiX, FiSearch } from "react-icons/fi";
import { BsFillXCircleFill } from "react-icons/bs";
import "../../styles/walletTransfer.css";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import { AuthContext } from "../../context/AuthContext";
import { selectCustomStyles } from "../../utils/utils";
import WalletTransferServices from "../../services/WalletTransferServices";
import TransactionHistoryServices from "../../services/TransactionHistoryServices";
import AdminUsersServices from "../../services/AdminUsersServices";
import OutletUsersServices from "../../services/OutletUsersServices";
import MobileUsersServices from "../../services/MobileUsersServices";

const WalletTransfer = () => {
  const walletTransferApiRef = useRef(null);
  if (!walletTransferApiRef.current) {
    walletTransferApiRef.current = new WalletTransferServices();
  }
  const txApi = new TransactionHistoryServices();
  const adminSerApi = new AdminUsersServices();
  const outletSerApi = new OutletUsersServices();
  const mobileSerApi = new MobileUsersServices();

  const { user } = useContext(AuthContext);
  const defaultFrom = moment().startOf("month").format("YYYY-MM-DD");
  const defaultTo = moment().format("YYYY-MM-DD");
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  /**
   * Mobile-only bottom-sheet filter UI. Desktop CSS hides every mobile-only
   * element (filter pill, backdrop, sheet) so the inline desktop header is
   * preserved exactly as before. The sheet renders its own copies of the
   * date pickers bound to the same state.
   */
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    if (!mobileFiltersOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileFiltersOpen]);

  const datePresets = useMemo(
    () => [
      {
        key: "today",
        label: "Today",
        from: moment().format("YYYY-MM-DD"),
        to: moment().format("YYYY-MM-DD"),
      },
      {
        key: "yesterday",
        label: "Yesterday",
        from: moment().subtract(1, "day").format("YYYY-MM-DD"),
        to: moment().subtract(1, "day").format("YYYY-MM-DD"),
      },
      {
        key: "7d",
        label: "Last 7 days",
        from: moment().subtract(6, "days").format("YYYY-MM-DD"),
        to: moment().format("YYYY-MM-DD"),
      },
      {
        key: "this_month",
        label: "This month",
        from: moment().startOf("month").format("YYYY-MM-DD"),
        to: moment().format("YYYY-MM-DD"),
      },
      {
        key: "last_month",
        label: "Last month",
        from: moment()
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD"),
        to: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      },
    ],
    [],
  );

  const activePresetKey = useMemo(() => {
    const match = datePresets.find((p) => p.from === from && p.to === to);
    return match ? match.key : null;
  }, [datePresets, from, to]);

  const [walletPayment, setWalletPayment] = useState("Receipt");
  const [amount, setAmount] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [dropdownUsers, setDropdownUsers] = useState([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [balanceLog, setBalanceLog] = useState([]);
  const [userId, setUserId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [q, setQ] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [loading, setLoading] = useState(false);
  const [adminWalletBalance, setAdminWalletBalance] = useState(0.0);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (from && from !== defaultFrom) n += 1;
    if (to && to !== defaultTo) n += 1;
    if (paymentType) n += 1;
    if (q) n += 1;
    return n;
  }, [from, to, paymentType, q, defaultFrom, defaultTo]);

  const applyDatePreset = useCallback((preset) => {
    setFrom(preset.from);
    setTo(preset.to);
    setPage(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFrom(defaultFrom);
    setTo(defaultTo);
    setPaymentType("");
    setQ("");
    setPage(1);
  }, [defaultFrom, defaultTo]);

  const userOptions = useMemo(() => {
    const selfId = Number(user?.user_id);
    return (dropdownUsers || [])
      .filter((u) => Number(u.id) !== selfId)
      .map((u) => {
        const id = Number(u.id);
        const uname = u.username || "—";
        return {
          value: id,
          label: `${uname}`,
          raw: u,
        };
      });
  }, [dropdownUsers, user?.user_id]);

  const onAmountChange = (e) => {
    const value = e.target.value;
    const filter = value.replace(/[^0-9]/g, "");
    if (filter !== value) {
      toast.error("Please enter a valid amount in numbers");
      setAmount("");
      return;
    }
    setAmount(filter);
  };

  const onFrom = (e) => {
    setFrom(e.target.value);
  };

  const onTo = (e) => {
    setTo(e.target.value);
  };

  const walletSelectStyles = useMemo(
    () => ({
      ...selectCustomStyles,
      control: (base, state) => ({
        ...(typeof selectCustomStyles.control === "function"
          ? selectCustomStyles.control(base, state)
          : base),
        minWidth: "min(100%, 320px)",
        maxWidth: "100%",
        borderRadius: "10px",
        borderColor: "#fff",
        minHeight: "42px",
      }),
    }),
    [],
  );

  const loadBalanceLog = async () => {
    setLoading(true);
    try {
      const res = await txApi.getBalanceLogHistory({
        page,
        limit,
        q,
        from,
        to,
        userId,
        paymentType,
      });
      if (res.isSuccess) {
        setBalanceLog(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load balance log");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load balance log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBalanceLog();
  }, [page, limit, q, from, to, userId, paymentType]);

  const getDropdownUsers = useCallback(async () => {
    try {
      const res = await walletTransferApiRef.current.getDropdownUsers();
      if (res.isSuccess) {
        setDropdownUsers(res.data || []);
      } else {
        toast.error(res.message || "Failed to load users");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load users");
    }
  }, []);

  const getAdminWalletBalance = useCallback(async () => {
    try {
      const response = await adminSerApi.addAdminWalletBalanceById();

      if (response.isSuccess) {
        setAdminWalletBalance(response?.data?.wallet_balance);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to get admin wallet balance");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    getDropdownUsers();
    if (user?.role_id !== 1) {
      getAdminWalletBalance();
    }
  }, [getDropdownUsers]);

  const handleTransfer = async () => {
    if (!selectedUser?.value) {
      toast.error("Select a user to transfer balance");
      return;
    }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }

    if (
      user.role_id !== 1 &&
      walletPayment === "Receipt" &&
      Number(adminWalletBalance) <= amt
    ) {
      toast.error(
        `Please enter amount less than or equal to ${user?.username} wallet balance.`,
      );
      return;
    } else if (
      walletPayment === "Payment" &&
      Number(selectedUser?.raw?.wallet_balance) <= amt
    ) {
      toast.error(
        `Please enter amount less than or equal to ${selectedUser?.raw?.username} wallet balance.`,
      );
      return;
    }

    let finalAmount = 0;
    let type = "CREDIT";
    let remark = "";

    if (walletPayment === "Payment") {
      type = "DEBIT";
      remark = `Deduct Wallet Balance by ${user?.username}.`;
      if (user.role_id !== 1) {
        finalAmount = (Number(user?.wallet_balance) || 0) + (Number(amt) || 0);
      }
    } else {
      if (user.role_id !== 1) {
        finalAmount = (Number(user?.wallet_balance) || 0) - (Number(amt) || 0);
      }
      remark = `Add Wallet Balance by ${user?.username}.`;
    }

    const payload = {
      user_id: selectedUser?.raw?.id,
      wallet_payment: walletPayment,
      wallet_balance: amt,
      type: type,
      remark: remark,
      admin_wallet_balance: finalAmount,
    };

    setTransferLoading(true);
    try {
      const res =
        selectedUser?.raw?.role_id === 2
          ? await adminSerApi.addAdminWalletBalanceBySA(payload)
          : selectedUser?.raw?.role_id === 3
            ? await outletSerApi.addOutletWalletBalanceBySA(payload)
            : selectedUser?.raw?.role_id === 4
              ? await mobileSerApi.addMobileWalletBalanceBySA(payload)
              : null;
      if (res?.isSuccess) {
        toast.success(res.message || "Transfer completed");
        setAmount("");
        setWalletPayment("Receipt");
        loadBalanceLog();
        await getDropdownUsers();
        setSelectedUser((prev) => ({
          ...prev,
          raw: {
            ...prev?.raw,
            wallet_balance: res.balance,
          },
        }));
        setPage(1);
        if (user.role_id !== 1) {
          // setAdminWalletBalance(finalAmount);
          getAdminWalletBalance();
        }
      } else {
        toast.error(res?.message || "Transfer failed");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Transfer failed";
      toast.error(msg);
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <div className={`wt-page${mobileFiltersOpen ? " wt-filters-open" : ""}`}>
      <div className="wallet-transfer-header">
        <div>
          <div className="wallet-transfer-search">
            <Select
              inputId="wallet-transfer-user-select"
              instanceId="wallet-transfer-user"
              className="wallet-transfer-user-select"
              classNamePrefix="wt-select"
              styles={walletSelectStyles}
              options={userOptions}
              value={selectedUser}
              onChange={(opt) => {
                setSelectedUser(opt);
                setPage(1);
                setUserId(opt?.value ?? null);
                setBalanceLog([]);
                setPagination(null);
                setQ("");
                setPaymentType("");
                setAmount("");
                setWalletPayment("Receipt");
              }}
              placeholder="Search by username, or ID…"
              isClearable
              isSearchable
              filterOption={(candidate, input) => {
                if (!input) return true;
                const q = input.toLowerCase().trim();
                const r = candidate.data?.raw;
                if (!r)
                  return String(candidate.label).toLowerCase().includes(q);
                const hay = [r.name, r.username, String(r.id)]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase();
                return hay.includes(q);
              }}
              noOptionsMessage={() => "No users match"}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
            />

            <button
              type="button"
              className="wt-mobile-filter-btn"
              onClick={() => setMobileFiltersOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={mobileFiltersOpen}
              aria-label="Open balance log filters"
            >
              <FiFilter size={15} />
              {activeFilterCount > 0 && (
                <span className="wt-mfb-count">{activeFilterCount}</span>
              )}
            </button>
          </div>
          {selectedUser && (
            <div className="wallet-transfer-balance-container">
              <label>
                Balance:{" "}
                <span className="wallet-transfer-balance-amount">
                  {walletPayment === "Receipt"
                    ? Number(selectedUser?.raw?.wallet_balance) +
                      Number(amount || 0)
                    : Number(selectedUser?.raw?.wallet_balance) -
                      Number(amount || 0)}
                </span>
              </label>
            </div>
          )}
        </div>
        <div className="wallet-transfer-filters-container">
          <div className="wallet-transfer-filters">
            <div className="wallet-transfer-date">
              <label>From</label>
              <input type="date" value={from} onChange={onFrom} />
            </div>
            <div className="wallet-transfer-date">
              <label>To</label>
              <input type="date" value={to} onChange={onTo} />
            </div>
          </div>

          {selectedUser && (
            <>
              <div className="wallet-transfer-amount-container">
                <div className="input-wrapper">
                  <input
                    type="radio"
                    className="form-check-input me-1"
                    name="wallet_payment"
                    id="wallet_receipt"
                    value="Receipt"
                    checked={walletPayment === "Receipt"}
                    onChange={(e) => setWalletPayment(e.target.value)}
                  />
                  <label htmlFor="wallet_receipt">Receipt (Add Balance)</label>
                  <input
                    type="radio"
                    className="form-check-input ms-3 me-1"
                    name="wallet_payment"
                    id="wallet_payment"
                    value="Payment"
                    checked={walletPayment === "Payment"}
                    onChange={(e) => setWalletPayment(e.target.value)}
                  />
                  <label htmlFor="wallet_payment">
                    Payment (Deduct Balance)
                  </label>
                </div>

                <div>
                  <input
                    type="text"
                    placeholder="Enter amount"
                    className="wallet-transfer-amount"
                    value={amount}
                    onChange={onAmountChange}
                  />
                </div>
                {user.role_id !== 1 && (
                  <div>
                    {user.username} balance:
                    <span className="wallet-transfer-balance-amount">
                      {" "}
                      {walletPayment === "Receipt"
                        ? Number(adminWalletBalance) - Number(amount || 0)
                        : Number(adminWalletBalance) + Number(amount || 0)}
                    </span>
                  </div>
                )}
              </div>

              <div className="wallet-transfer-button-container">
                <button
                  type="button"
                  className="wallet-transfer-button"
                  onClick={handleTransfer}
                  disabled={transferLoading}
                >
                  {walletPayment === "Receipt"
                    ? transferLoading
                      ? "Transferring…"
                      : "Transfer"
                    : transferLoading
                      ? "Deducting…"
                      : "Deduct"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Active filter chips (mobile only). One-tap remove. */}
      {activeFilterCount > 0 && (
        <div className="wt-active-chips">
          {(from !== defaultFrom || to !== defaultTo) && (
            <span
              className={`wt-active-chip${activePresetKey ? " preset" : ""}`}
            >
              <span>
                {activePresetKey
                  ? datePresets.find((p) => p.key === activePresetKey)?.label
                  : `${moment(from).format("DD MMM")} – ${moment(to).format("DD MMM")}`}
              </span>
              <button
                type="button"
                aria-label="Reset date range"
                onClick={() => {
                  setFrom(defaultFrom);
                  setTo(defaultTo);
                  setPage(1);
                }}
              >
                <FiX size={12} />
              </button>
            </span>
          )}
          {paymentType && (
            <span className={`wt-active-chip ${paymentType.toLowerCase()}`}>
              <span>{paymentType === "CREDIT" ? "Credit" : "Debit"}</span>
              <button
                type="button"
                aria-label="Clear transaction type"
                onClick={() => {
                  setPaymentType("");
                  setPage(1);
                }}
              >
                <FiX size={12} />
              </button>
            </span>
          )}
          {q && (
            <span className="wt-active-chip">
              <span>“{q}”</span>
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setQ("");
                  setPage(1);
                }}
              >
                <FiX size={12} />
              </button>
            </span>
          )}
        </div>
      )}

      {/* ===== MOBILE ONLY — Bottom-sheet backdrop ===== */}
      <div
        className="wt-sheet-backdrop"
        onClick={() => setMobileFiltersOpen(false)}
        aria-hidden="true"
      />

      {/* ===== MOBILE ONLY — Bottom sheet ===== */}
      <aside
        className="wt-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Filter balance log"
      >
        <div className="wt-sheet-grab" aria-hidden="true">
          <span />
        </div>
        <div className="wt-sheet-head">
          <div className="wt-sheet-head-left">
            <span className="wt-sheet-icon" aria-hidden="true">
              <FiFilter size={16} />
            </span>
            <div className="wt-sheet-titles">
              <h4 className="wt-sheet-title">
                Filters
                {activeFilterCount > 0 && (
                  <span className="wt-sheet-count">{activeFilterCount}</span>
                )}
              </h4>
              <p className="wt-sheet-subtitle">
                {activeFilterCount > 0
                  ? `${activeFilterCount} active filter${activeFilterCount > 1 ? "s" : ""}`
                  : "Refine your balance log"}
              </p>
            </div>
          </div>
          {activeFilterCount > 0 && (
            <button
              type="button"
              className="wt-sheet-reset"
              onClick={clearAllFilters}
            >
              Reset
            </button>
          )}
          <button
            type="button"
            className="wt-sheet-close"
            onClick={() => setMobileFiltersOpen(false)}
            aria-label="Close filters"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="wt-sheet-body">
          {/* Quick date presets */}
          <div className="wt-sheet-section">
            <div className="wt-sheet-section-label">Quick presets</div>
            <div className="wt-preset-row">
              {datePresets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`wt-preset-chip${activePresetKey === p.key ? " active" : ""}`}
                  onClick={() => applyDatePreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom date range */}
          <div className="wt-sheet-section">
            <div className="wt-sheet-section-label">Custom date range</div>
            <div className="wt-sheet-dates">
              <div className="wt-sheet-date">
                <label htmlFor="wt-from">From</label>
                <input
                  id="wt-from"
                  type="date"
                  value={from}
                  onChange={onFrom}
                />
              </div>
              <div className="wt-sheet-date">
                <label htmlFor="wt-to">To</label>
                <input id="wt-to" type="date" value={to} onChange={onTo} />
              </div>
            </div>
          </div>

          {/* Transaction type chips */}
          <div className="wt-sheet-section">
            <div className="wt-sheet-section-label">Transaction type</div>
            <div className="wt-sheet-chips">
              {[
                { v: "", label: "All" },
                { v: "CREDIT", label: "Credit" },
                { v: "DEBIT", label: "Debit" },
              ].map((o) => (
                <button
                  key={o.v || "all"}
                  type="button"
                  className={`wt-chip${paymentType === o.v ? " active" : ""}${
                    o.v ? ` ${o.v.toLowerCase()}` : ""
                  }`}
                  onClick={() => {
                    setPaymentType(o.v);
                    setPage(1);
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Free text search */}
          <div className="wt-sheet-section">
            <div className="wt-sheet-section-label">Search</div>
            <div className="wt-sheet-search">
              <FiSearch size={16} className="wt-sheet-search-icon" />
              <input
                type="text"
                placeholder="Reference, remark, type…"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
              />
              {q && (
                <button
                  type="button"
                  className="wt-sheet-search-clear"
                  aria-label="Clear search"
                  onClick={() => {
                    setQ("");
                    setPage(1);
                  }}
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="wt-sheet-foot">
          <button
            type="button"
            className="wt-sheet-btn ghost"
            onClick={clearAllFilters}
            disabled={activeFilterCount === 0}
          >
            <BsFillXCircleFill size={14} />
            <span>Clear all</span>
          </button>
          <button
            type="button"
            className="wt-sheet-btn primary"
            onClick={() => setMobileFiltersOpen(false)}
          >
            Done
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        </div>
      </aside>

      {!selectedUser ? (
        <NoData
          message="No User Selected"
          subText="Select a user to transfer to."
        />
      ) : (
        <>
          <div>
            {loading ? (
              <Loader message="Loading balance log..." />
            ) : balanceLog.length === 0 ? (
              <NoData
                message="No balance log found"
                subText="Try changing date range or select another user."
              />
            ) : (
              <>
                <div className="tx-table-bpl-scroll">
                  <div className="tx-table-header-blog">
                    <div>Date</div>
                    <div>Reference</div>
                    <div>Type</div>
                    <div>Amount</div>
                    <div>Balance</div>
                    <div style={{ minWidth: "210px" }}>Remark</div>
                  </div>

                  <div className="tx-table-blog">
                    {balanceLog.map((r) => {
                      const isCredit =
                        String(r.txn_type).toUpperCase() === "CREDIT";
                      return (
                        <div className="tx-row-blog" key={r.id}>
                          <div>
                            <div style={{ fontWeight: 700 }}>
                              {moment
                                .utc(r.created_at)
                                .local()
                                .format("DD-MMM-YYYY HH:mm A")}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontWeight: 700 }}>
                              {(() => {
                                const ref = r?.reference_id;
                                if (ref == null || ref === "") return "—";
                                const s = String(ref);
                                return s.length > 13
                                  ? `${s.slice(0, 13)}...`
                                  : s;
                              })()}
                            </div>
                            <div className="tx-muted">
                              {r.reference_type || "-"}
                            </div>
                          </div>
                          <div style={{ fontWeight: 700 }}>{r.txn_type}</div>
                          <div
                            className={`tx-amount ${isCredit ? "credit" : "debit"}`}
                          >
                            {isCredit ? "+" : "-"}
                            {r.amount}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700 }}>
                              {r.balance_after}
                            </div>
                            <div className="tx-muted">
                              Before: {r.balance_before}
                            </div>
                          </div>
                          <div style={{ minWidth: "210px" }}>
                            {r.remark || "-"}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {pagination && pagination.totalPages > 1 && (
                    <div className="modern-pagination">
                      {/* Limit Selector */}
                      <div className="mp-limit">
                        <span>Show</span>
                        <select
                          value={limit}
                          onChange={(e) => setLimit(e.target.value)}
                        >
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                          <option value="200">200</option>
                          <option value="500">500</option>
                          <option value="1000">1000</option>
                        </select>
                      </div>

                      {/* Navigation */}
                      <div className="mp-controls">
                        <button
                          disabled={page === 1}
                          onClick={() => setPage(page - 1)}
                        >
                          Prev
                        </button>

                        <div className="mp-info">
                          Page <strong>{pagination.page}</strong> of{" "}
                          <strong>{pagination.totalPages}</strong>
                        </div>

                        <button
                          disabled={page === pagination.totalPages}
                          onClick={() => setPage(page + 1)}
                        >
                          Next
                        </button>
                      </div>

                      {/* Jump to Page */}
                      <div className="mp-jump">
                        <select
                          value={page}
                          onChange={(e) => setPage(e.target.value)}
                        >
                          {Array.from(
                            { length: pagination.totalPages },
                            (_, i) => (
                              <option key={i + 1} value={i + 1}>
                                Page {i + 1}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WalletTransfer;
